import React from "react";

import { fetchGeotabPreferences, saveGeotabPreferences } from "../api/geotabApi.js";
import { TOLERANCE_MIN } from "../components/geotab/geotabFormat.js";
import { onPreferencesTabReturn } from "../utils/preferencesDelegation.js";
import { subscribeUserUiPreferences } from "../lib/userUiPreferencesSync.js";

// Tolérance des écarts Geotab : le réglage « Tolérance » de la section Geotab, aussi réglable dans le menu Affichage.
// Une seule valeur pour les écarts du Gantt, les avis en direct et la section Geotab : mêmes couleurs partout.
let tolerance = TOLERANCE_MIN;
let toleranceRevision = 0;
let loading = null;
const listeners = new Set();
const emit = () => listeners.forEach((listener) => listener());
const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
const snapshot = () => tolerance;
let saving = false;

subscribeUserUiPreferences("geotab", (value) => {
  if (saving) return false;
  rememberGeotabTolerance(value?.tolerance_min || TOLERANCE_MIN);
  return true;
});

function load() {
  const revision = toleranceRevision;
  loading ||= fetchGeotabPreferences()
    .then((preferences) => {
      if (revision !== toleranceRevision) return;
      if (!preferences?.tolerance_min || preferences.tolerance_min === tolerance) return;
      tolerance = preferences.tolerance_min;
      emit();
    })
    .catch(() => { loading = null; });
}

// Un Admin peut l'avoir réglée pour l'utilisateur : relue au retour sur l'onglet.
onPreferencesTabReturn(() => {
  if (!loading) return;
  loading = null;
  load();
});

// Changée dans la section Geotab (qui l'enregistre elle-même) : le Gantt et les avis suivent tout de suite.
export function rememberGeotabTolerance(value) {
  if (!value || value === tolerance) return;
  tolerance = value;
  toleranceRevision++;
  emit();
}

// Relit les autres réglages de la section juste avant d'enregistrer : seule la tolérance change.
export async function setGeotabTolerance(value) {
  saving = true;
  toleranceRevision++;
  const previous = tolerance;
  tolerance = value;
  emit();
  try {
    const current = await fetchGeotabPreferences();
    await saveGeotabPreferences({ ...(current || {}), tolerance_min: value });
  } catch (error) {
    if (tolerance === value) {
      tolerance = previous;
      emit();
    }
    throw error;
  } finally {
    saving = false;
  }
}

export default function useGeotabTolerance(enabled = true) {
  const value = React.useSyncExternalStore(subscribe, snapshot);
  React.useEffect(() => { if (enabled) load(); }, [enabled]);
  return value;
}
